// Iter47-A — Shared master-data table for SR types / Tenant types / Incident categories.
// All three follow the same shape: list of rows with {key, names{en,de,hu}, system}
// plus per-type extras (icon/module, layer/incident_role, default_severity, …).
// Locked rows (system=true) cannot be deleted but their labels + config remain editable.
import React, { useState } from "react";
import { Plus, Edit2, Trash2, Lock, Check, X } from "lucide-react";

export const mono = (size, color, weight, ls) => ({
  fontFamily: "DM Mono, monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

export const inputStyle = {
  background: "var(--bg-base, #08090d)",
  border: "1px solid rgba(255,255,255,0.10)",
  color: "var(--text, #f0f2f8)",
  ...mono(11),
  padding: "6px 10px",
  borderRadius: 6,
  width: "100%",
  outline: "none",
};

export const Label = ({ children }) => (
  <div className="mb-1 uppercase" style={mono(8, "var(--dimmer, #9198b0)", 700, "0.8px")}>
    {children}
  </div>
);

// Tri-locale name input row (EN/DE/HU).
export const NameRow = ({ names, onChange, testIdPrefix }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
    {[
      { code: "en", label: "Name (EN)" },
      { code: "de", label: "Name (DE)" },
      { code: "hu", label: "Name (HU)" },
    ].map((l) => (
      <div key={l.code}>
        <Label>{l.label}</Label>
        <input
          type="text"
          value={names[l.code] || ""}
          onChange={(e) => onChange({ ...names, [l.code]: e.target.value })}
          style={inputStyle}
          required={l.code === "en"}
          data-testid={`${testIdPrefix}-name-${l.code}`}
        />
      </div>
    ))}
  </div>
);

export const SystemBadge = () => (
  <span className="px-1.5 py-0.5 rounded inline-flex items-center gap-1"
    style={{ background: "rgba(243,177,74,0.12)", color: "#f3b14a", ...mono(8, undefined, 700, "0.5px") }}>
    <Lock size={8} /> SYSTEM
  </span>
);

export const RowActions = ({ row, onEdit, onDelete, canDelete = true, testIdPrefix }) => (
  <div className="flex justify-end gap-1">
    <button type="button" onClick={onEdit}
      className="px-2 py-1 rounded inline-flex items-center gap-1"
      style={{ background: "transparent", color: "#9ec3ec", border: "1px solid rgba(24,95,165,0.40)", ...mono(9, undefined, 700, "0.4px"), cursor: "pointer" }}
      data-testid={`${testIdPrefix}-edit-${row.key}`}><Edit2 size={10} /> Edit</button>
    <button type="button" onClick={onDelete}
      disabled={!canDelete}
      title={canDelete ? "Delete" : "System rows can't be deleted"}
      className="px-2 py-1 rounded inline-flex items-center gap-1"
      style={{
        background: "transparent",
        color: canDelete ? "#ff9a99" : "#5a6072",
        border: `1px solid ${canDelete ? "rgba(226,75,74,0.40)" : "rgba(255,255,255,0.06)"}`,
        ...mono(9, undefined, 700, "0.4px"),
        cursor: canDelete ? "pointer" : "not-allowed",
      }}
      data-testid={`${testIdPrefix}-delete-${row.key}`}><Trash2 size={10} /> Del</button>
  </div>
);

export const NewRowButton = ({ onClick, label = "Add", testId }) => (
  <button
    type="button"
    onClick={onClick}
    className="px-3 py-1.5 rounded inline-flex items-center gap-1.5"
    style={{ background: "#185FA5", color: "#fff", ...mono(10, undefined, 700, "0.4px"), border: "none", cursor: "pointer" }}
    data-testid={testId}
  >
    <Plus size={12} /> {label}
  </button>
);

export const SaveCancelButtons = ({ onSave, onCancel, busy, testIdPrefix }) => (
  <div className="flex justify-end gap-2 mt-2">
    <button type="button" onClick={onCancel}
      className="px-3 py-1.5 rounded"
      style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "var(--text-dim, #c5cad8)", ...mono(10, undefined, 600, "0.4px"), cursor: "pointer" }}
      data-testid={`${testIdPrefix}-cancel`}><X size={11} className="inline-block mr-1" /> Cancel</button>
    <button type="button" onClick={onSave} disabled={busy}
      className="px-3 py-1.5 rounded"
      style={{ background: "#185FA5", color: "#fff", ...mono(10, undefined, 700, "0.4px"), border: "none", cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1 }}
      data-testid={`${testIdPrefix}-save`}><Check size={11} className="inline-block mr-1" /> Save</button>
  </div>
);

// Pick the right locale name (with EN fallback).
export const pickName = (row, locale = "en") => {
  if (!row?.names) return row?.key || "—";
  return row.names[locale] || row.names.en || row.key;
};
